import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../utils/auth';
import api, { sessions as sessionsApi } from '../utils/api';
import { useSocket } from '../utils/socket';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Play, Square, Send, ExternalLink, Clock, Settings, Trash2, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

function fmtTime(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function timerColor(remaining, total) {
  if (total <= 0) return 'var(--teal)';
  const pct = remaining / total;
  if (pct > 0.33) return 'var(--teal)';
  if (pct > 0.15) return 'var(--amber)';
  return 'var(--red)';
}

// ── Modale configuration Live ─────────────────────────────────
function LiveConfigModal({ room, onClose, onSaved }) {
  const [config, setConfig] = useState(room.live_config || {});
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sessionsApi.getHintTemplates(room.id).then(r =>
      setTemplates(r.data.templates.map(t => ({ id: t.id, content: t.content })))
    );
  }, [room.id]);

  const addTemplate = () => setTemplates(p => [...p, { content: '' }]);
  const updTemplate = (idx, val) => setTemplates(p => p.map((t,i) => i===idx ? {...t, content: val} : t));
  const delTemplate = (idx) => setTemplates(p => p.filter((_,i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        sessionsApi.setLiveConfig(room.id, config),
        sessionsApi.setHintTemplates(room.id, { templates: templates.filter(t => t.content.trim()) }),
      ]);
      toast.success('Configuration sauvegardée');
      onSaved(); onClose();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const f = k => ({ value: config[k] || '', onChange: e => setConfig(p => ({...p, [k]: e.target.value})) });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <h3 className="modal-title">Configuration Live — {room.name}</h3>

        <div className="form-group">
          <label className="form-label">Orientation de l'écran</label>
          <div style={{ display:'flex', gap:8 }}>
            {[['landscape','🖥 Paysage'],['portrait','📱 Portrait']].map(([val, label]) => (
              <button key={val} type="button" className={`btn ${config.orientation === val ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex:1 }} onClick={() => setConfig(p => ({...p, orientation: val}))}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Image de fond (URL)</label>
          <input className="input" {...f('bgImage')} placeholder="https://… ou laisser vide"/>
          <span className="text-xs text-muted">Import de fichier disponible prochainement</span>
        </div>

        <div className="form-group">
          <label className="form-label">Son indice (URL)</label>
          <input className="input" {...f('hintSound')} placeholder="URL .mp3 ou laisser vide (son par défaut)"/>
        </div>

        <div className="form-group">
          <label className="form-label">Son alarme Hors-Jeu (URL)</label>
          <input className="input" {...f('alarmSound')} placeholder="URL .mp3 ou laisser vide (son par défaut)"/>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ marginBottom:8 }}>Indices prédéfinis</label>
          <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:8 }}>
            {templates.map((t, idx) => (
              <div key={idx} style={{ display:'flex', gap:6 }}>
                <input className="input" style={{ flex:1 }} value={t.content}
                  placeholder={`Indice ${idx+1}…`}
                  onChange={e => updTemplate(idx, e.target.value)}/>
                <button className="btn btn-ghost btn-sm" onClick={() => delTemplate(idx)}>
                  <Trash2 size={13} color="var(--red)"/>
                </button>
              </div>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={addTemplate}>
            + Ajouter un indice
          </button>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" style={{width:16,height:16}}/> : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Panneau d'une salle ───────────────────────────────────────
function RoomTimer({ room }) {
  const { socket }          = useSocket() || {};
  const [session, setSession]     = useState(null);
  const [hints, setHints]         = useState([]);
  const [hintTemplates, setHintTemplates] = useState([]);
  const [elapsed, setElapsed]     = useState(0);
  const [adjustment, setAdj]      = useState(0); // secondes ajoutées/retirées
  const [hintText, setHintText]   = useState('');
  const [showConfig, setConfig]   = useState(false);
  const [alarmActive, setAlarm]   = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const hintsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await sessionsApi.current(room.id);
      setSession(r.data.session);
      setHints(r.data.hints || []);
    } catch {}
    sessionsApi.getHintTemplates(room.id)
      .then(r => setHintTemplates(r.data.templates))
      .catch(() => {});
  }, [room.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join:room', room.id);
    const onStarted = (data) => { if (data.roomId === room.id) { setAdj(0); load(); } };
    const onStopped = (data) => { if (data.roomId === room.id) { setSession(null); setHints([]); setAdj(0); } };
    const onHint    = (data) => { if (data.sessionId === session?.id) setHints(prev => [...prev, data.hint]); };
    const onHintDel = (data) => { if (data.sessionId === session?.id) setHints(prev => prev.filter(h => h.id !== data.hintId)); };
    const onAlarm   = (data) => { if (data.roomId === room.id) setAlarm(data.active); };
    socket.on('session:started', onStarted);
    socket.on('session:stopped', onStopped);
    socket.on('session:hint', onHint);
    socket.on('session:hint_deleted', onHintDel);
    socket.on('session:alarm', onAlarm);
    return () => {
      socket.emit('leave:room', room.id);
      socket.off('session:started', onStarted);
      socket.off('session:stopped', onStopped);
      socket.off('session:hint', onHint);
      socket.off('session:hint_deleted', onHintDel);
      socket.off('session:alarm', onAlarm);
    };
  }, [socket, room.id, session?.id, load]);

  useEffect(() => {
    if (!session) { setElapsed(0); return; }
    const update = () => {
      const e = Math.floor((Date.now() - new Date(session.started_at).getTime()) / 1000);
      setElapsed(e);
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [session]);

  useEffect(() => {
    if (hintsRef.current) hintsRef.current.scrollTop = hintsRef.current.scrollHeight;
  }, [hints]);

  const start = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const r = await api.get('/bookings', { params: { date: today } });
      const booking = r.data.bookings?.find(b =>
        b.room_id === room.id && ['confirmed','pending'].includes(b.status)
      );
      if (!booking) { toast.error('Aucune réservation confirmée aujourd\'hui pour cette salle'); return; }
      await sessionsApi.start(booking.id);
      toast.success('Session démarrée'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const stop = async () => {
    if (!session) return;
    setConfirmStop(false);
    try { await sessionsApi.stop(session.id); setSession(null); setHints([]); setAdj(0); }
    catch { toast.error('Erreur'); }
  };

  const adjust = async (delta) => {
    if (!session) return;
    try {
      await sessionsApi.adjust(session.id, delta);
      setAdj(prev => prev + delta); // mise à jour locale immédiate
    } catch { toast.error('Erreur ajustement'); }
  };

  const sendHint = async (content) => {
    const text = (content || hintText).trim();
    if (!text || !session) return;
    try {
      await sessionsApi.sendHint(session.id, { content: text });
      if (!content) setHintText('');
    } catch { toast.error('Erreur envoi indice'); }
  };

  const deleteHint = async (hintId) => {
    if (!session) return;
    try { await sessionsApi.deleteHint(session.id, hintId); }
    catch { toast.error('Erreur'); }
  };

  const triggerAlarm = async (active) => {
    if (!session) return;
    try {
      await sessionsApi.alarm(session.id, { active });
      setAlarm(active);
    } catch { toast.error('Erreur'); }
  };

  const durationSec = (session?.duration_sec || room.duration_min * 60 || 3600) + adjustment;
  const remaining   = Math.max(0, durationSec - elapsed);
  const color       = session ? timerColor(remaining, durationSec) : 'var(--text-3)';
  const pct         = session ? Math.min(100, (elapsed / durationSec) * 100) : 0;

  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      {/* En-tête */}
      <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)',
        display:'flex', alignItems:'center', gap:10 }}>
        <h3 style={{ fontWeight:700, fontSize:16, flex:1 }}>{room.name}</h3>
        {session && <span className="badge badge-green" style={{ fontSize:11 }}>En cours</span>}
        <button className="btn btn-ghost btn-sm" onClick={() => setConfig(true)}>
          <Settings size={14}/>
        </button>
        <button className="btn btn-ghost btn-sm"
          onClick={() => window.open('/live/'+room.id, 'live_'+room.id, 'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no')}>
          <ExternalLink size={14}/> Live
        </button>
      </div>

      <div style={{ padding:20 }}>
        {/* Barre + Timer */}
        <div style={{ textAlign:'center', marginBottom:16 }}>
          <div style={{ height:6, background:'var(--bg-elevated)', borderRadius:3, marginBottom:14, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:color,
              borderRadius:3, transition:'width 1s linear, background 0.5s' }}/>
          </div>
          <div style={{ fontFamily:'monospace', fontSize:52, fontWeight:700, color, lineHeight:1, marginBottom:6 }}>
            {fmtTime(remaining)}
          </div>
          {session && (
            <div style={{ fontSize:12, color:'var(--text-3)' }}>
              Démarré à {format(new Date(session.started_at), 'HH:mm', { locale:fr })}
              {adjustment !== 0 && (
                <span style={{ marginLeft:8, color: adjustment > 0 ? 'var(--teal)' : 'var(--red)' }}>
                  ({adjustment > 0 ? '+' : ''}{Math.floor(adjustment/60)} min)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Contrôles */}
        <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:16, flexWrap:'wrap' }}>
          {!session ? (
            <button className="btn btn-primary" onClick={start} style={{ padding:'10px 28px' }}>
              <Play size={16}/> Démarrer
            </button>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => adjust(-60)}>−1 min</button>
              <button className="btn btn-secondary btn-sm" onClick={() => adjust(-300)}>−5 min</button>
              <button className="btn btn-danger" onClick={() => setConfirmStop(true)} style={{ padding:'8px 20px' }}>
                <Square size={14}/> Arrêter
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => adjust(300)}>+5 min</button>
              <button className="btn btn-secondary btn-sm" onClick={() => adjust(60)}>+1 min</button>
            </>
          )}
        </div>

        {/* Bouton Hors-Jeu */}
        {session && (
          <div style={{ marginBottom:16 }}>
            {!alarmActive ? (
              <button className="btn btn-danger w-full" style={{ justifyContent:'center', padding:'10px' }}
                onClick={() => triggerAlarm(true)}>
                <AlertTriangle size={15}/> Hors-Jeu
              </button>
            ) : (
              <button className="btn btn-secondary w-full" style={{ justifyContent:'center', padding:'10px',
                borderColor:'var(--red)', color:'var(--red)' }}
                onClick={() => triggerAlarm(false)}>
                <X size={15}/> Désactiver Hors-Jeu
              </button>
            )}
          </div>
        )}

        {/* Indices */}
        {session && (
          <div>
            {/* Templates rapides */}
            {hintTemplates.length > 0 && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                {hintTemplates.map(t => (
                  <button key={t.id} className="btn btn-secondary btn-sm"
                    style={{ fontSize:11 }} onClick={() => sendHint(t.content)}>
                    💡 {t.content.length > 30 ? t.content.slice(0,30)+'…' : t.content}
                  </button>
                ))}
              </div>
            )}

            {/* Liste indices envoyés */}
            <div style={{ fontWeight:600, fontSize:12, marginBottom:6, color:'var(--text-3)',
              textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Indices envoyés ({hints.length})
            </div>
            <div ref={hintsRef} style={{ maxHeight:120, overflowY:'auto', marginBottom:8,
              background:'var(--bg-base)', borderRadius:8, padding:6 }}>
              {hints.length === 0 ? (
                <p className="text-sm text-muted" style={{ textAlign:'center', padding:'8px 0' }}>
                  Aucun indice envoyé
                </p>
              ) : hints.map(h => (
                <div key={h.id} style={{ display:'flex', alignItems:'center', gap:6,
                  padding:'5px 8px', marginBottom:3, background:'var(--bg-elevated)', borderRadius:6 }}>
                  <span style={{ flex:1, fontSize:12 }}>💡 {h.content}</span>
                  <span style={{ fontSize:10, color:'var(--text-3)', flexShrink:0 }}>
                    {format(new Date(h.sent_at), 'HH:mm')}
                  </span>
                  <button className="btn btn-ghost btn-sm" style={{ padding:2, flexShrink:0 }}
                    onClick={() => deleteHint(h.id)}>
                    <X size={11} color="var(--text-3)"/>
                  </button>
                </div>
              ))}
            </div>

            {/* Saisie indice libre */}
            <div style={{ display:'flex', gap:6 }}>
              <input className="input" style={{ flex:1, fontSize:13 }}
                placeholder="Envoyer un indice…"
                value={hintText} onChange={e => setHintText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); sendHint(); } }}/>
              <button className="btn btn-primary btn-sm" onClick={() => sendHint()} disabled={!hintText.trim()}>
                <Send size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfig && (
        <LiveConfigModal room={room} onClose={() => setConfig(false)} onSaved={load}/>
      )}
      {confirmStop && (
        <ConfirmModal
          title="Arrêter la session"
          message="Arrêter la session en cours ?"
          confirmLabel="Arrêter"
          danger
          onConfirm={stop}
          onCancel={() => setConfirmStop(false)}
        />
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function TimerPage() {
  const { can } = useAuth();
  if (!can('timer.manage')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    api.get('/rooms').then(r => setRooms(r.data.rooms.filter(r => r.is_active)));
  }, []);

  return (
    <>
      <div className="page-header">
        <div><h2>Timer Salles</h2><p>Gestion des sessions en cours</p></div>
      </div>
      <div className="page-body">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:20 }}>
          {rooms.map(room => <RoomTimer key={room.id} room={room}/>)}
          {rooms.length === 0 && <div className="empty-state"><p>Aucune salle active</p></div>}
        </div>
      </div>
    </>
  );
}
